/*
 * Copyright 2017-present Evernote Corporation. All rights reserved.
 */
import createLogger from 'ion-logger';
import SubscriberList from './lib/subscriberList';

import Message from './message';
import Subscriber from './subscriber';
import MessageQueue from './lib/messageQueue';

import { RegistrantHandler, SubscriberHandler } from './types';

const logger = createLogger('ion-broker:topic');

// filter for specified topics to not save data when published
const DISABLE_SAVE_TOPIC_LIST = ['message'];

/**
 * A topic within the PubSub Broker.
 * A topic is a 'channel' of sorts that can be subscribed and published to.
 * Topics can have subtopics as well.
 */
class Topic {
  name: string;
  subscribers: SubscriberList;
  messages: MessageQueue<any>;
  registrant: RegistrantHandler | null | undefined;
  children: Map<string, Topic>;

  constructor(name: string) {
    this.name = name;
    this.subscribers = new SubscriberList();
    this.children = new Map();
    this.messages = new MessageQueue();
  }

  subscribe(handler: SubscriberHandler, replayMessage: boolean = true): () => void {
    const subscriber = new Subscriber(handler);
    const unsubscribe = this.subscribers.subscribe(subscriber);

    if (replayMessage) {
      this._replay(subscriber);
    }

    return unsubscribe;
  }

  /**
   * Clears all stored messages for a topic
   */
  clearTopics() {
    logger.log(`Clearing all messages for topic ${this.name}`);
    this.messages.clearMessages();
  }

  /**
   * Publishes a message to currently attached subscribers, and saves that
   * message if needed so it can be replayed for subscribers who may join in the future
   *
   * topicPaths: The original paths that the caller requested
   * that the message be published to
   * type: The type of the message to send to the topic.
   * data: The data payload to send along with the message.
   * saveMessage: If true message will be saved and can be replayed to future subscribers,
                  if this flag is not set, messages with topic on the filter list
                  won't be saved, other messages will be saved.
   */
  publish<T>(
    topicPaths: string,
    type: string,
    data: T,
    saveMessage: boolean = !DISABLE_SAVE_TOPIC_LIST.includes(topicPaths)
  ): void {
    const message = new Message(topicPaths, type, data);

    // Save message to queue and then publish to subscribers
    if (saveMessage) {
      this._saveMessage(message);
    }

    try {
      this.subscribers.forEach(s => s.handler(message));
    } catch (e) {
      logger.log(`Error caught in Broker subscription handler: ${e.message}`);
    }
  }

  /**
   * Registers a handler to a topic, which can then be activated with a
   * subsequent invocation of call
   * @method
   * @param  {RegistrantHandler} handler The function that shold be invoked when
   * a 'call' is requested. Must return a promise!
   * @return {Function}   The returned function causes the registered handler to
   * be unregistered
   */
  register(handler: RegistrantHandler): () => void {
    this.registrant = handler;

    // Returns a func that if called will unregister the handler
    return () => {
      this.registrant = undefined;
    };
  }

  /**
   * Calls a previously registered handler on a topic
   * @method
   * @param {any} data The data to pass to the handler function
   * @return {Promise<any>}  The returned promise will resolve to the result
   * of the handler that was previously registered to the topic.
   */
  call(data?: any): Promise<any> {
    if (!this.registrant) {
      /*
       * Registrant is undefined or does not exist, either because it was never
       * registered with Broker.register or because the unregister function was called
       */
      throw new Error(
        `The topic ${this.name} does not have a registrant. ` +
          'Did you unregister the handler?'
      );
    }

    return this.registrant(data);
  }

  /**
   * Private method that will later provide a hook to allow us to persist
   * messages in IndexedDB or other means of local storage if we want to
   * @method
   * @param  {Message<T>} message The message to save to the queue
   * @return {void}
   */
  _saveMessage<T>(message: Message<T>): void {
    logger.log(`Saving message to topic ${this.name} as there are not yet any
      subscribers to receive it.`);
    this.messages.add(message);
  }

  _replay(subscriber: Subscriber): void {
    logger.log(`Replaying messages from topic ${this.name} for subscriber`);
    // Replay messages
    this.messages.forEach(m => subscriber.handler(m));
  }
}

export default Topic;
